import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Mail, Send, Loader2, CheckCircle2 } from "lucide-react";
import api from "@/lib/api";
import { Section, DeleteBtn } from "./common";
import EnquiriesList from "./EnquiriesList";
import { TabsContent } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";

export default function FansTab() {
  const [subs, setSubs] = useState([]);
  const [announcements, setAnnouncements] = useState([]);
  const [blasting, setBlasting] = useState(null);

  const load = async () => {
    try {
      const [s, a] = await Promise.all([api.get("/subscribers"), api.get("/announcements")]);
      setSubs(s.data);
      setAnnouncements(a.data);
    } catch {
      toast.error("Failed to load fans data");
    }
  };

  useEffect(() => { load(); }, []);

  const blast = async (ann) => {
    if (!window.confirm(`Email this announcement to all ${subs.length} subscriber(s)?\n\n"${ann.text}"`)) return;
    setBlasting(ann.id);
    try {
      const { data } = await api.post("/subscribers/blast", { announcement_id: ann.id });
      toast.success(`Sent to ${data.sent} fan(s)${data.failed ? ` — ${data.failed} failed` : ""}`);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Blast failed");
    } finally {
      setBlasting(null);
    }
  };

  return (
    <TabsContent value="fans" data-testid="admin-fans-tab">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Section title={`Notify list (${subs.length})`}>
          <ul className="divide-y divide-black/5" data-testid="admin-subscribers-list">
            {subs.map((s) => (
              <li key={s.id} className="py-3 flex items-center justify-between gap-3" data-testid={`admin-subscriber-${s.id}`}>
                <div className="flex items-center gap-3 min-w-0">
                  <span className="w-8 h-8 rounded-full bg-league-blue/10 text-league-blue flex items-center justify-center shrink-0"><Mail size={14} /></span>
                  <div className="min-w-0">
                    <p className="text-sm font-semibold text-league-ink truncate">{s.email}</p>
                    <p className="text-xs text-muted-foreground">via {s.source}</p>
                  </div>
                </div>
                <DeleteBtn testid={`admin-subscriber-delete-${s.id}`} onClick={async () => { await api.delete(`/subscribers/${s.id}`).catch(() => toast.error("Delete failed")); load(); }} />
              </li>
            ))}
            {subs.length === 0 && <li className="py-4 text-sm text-muted-foreground">No subscribers yet — fans join from the Live and Auction pages.</li>}
          </ul>
        </Section>
        <Section title="Send an announcement blast">
          <p className="text-xs text-muted-foreground mb-4">Pick an announcement to email it to every subscriber. Each announcement can only be blasted once.</p>
          <ul className="divide-y divide-black/5" data-testid="admin-blast-list">
            {announcements.map((a) => (
              <li key={a.id} className="py-3 flex items-start justify-between gap-3" data-testid={`admin-blast-item-${a.id}`}>
                <p className="text-sm text-league-ink leading-snug">{a.text}</p>
                {a.emailed_at ? (
                  <span className="shrink-0 inline-flex items-center gap-1.5 text-xs font-bold text-green-600" data-testid={`admin-blast-sent-${a.id}`}>
                    <CheckCircle2 size={14} /> Sent to {a.emailed_count ?? 0}
                  </span>
                ) : (
                  <Button size="sm" onClick={() => blast(a)} disabled={blasting === a.id || subs.length === 0} data-testid={`admin-blast-send-${a.id}`} className="shrink-0 gap-2">
                    {blasting === a.id ? <Loader2 size={13} className="animate-spin" /> : <Send size={13} />} Email fans
                  </Button>
                )}
              </li>
            ))}
            {announcements.length === 0 && <li className="py-4 text-sm text-muted-foreground">Post an announcement first, then blast it from here.</li>}
          </ul>
        </Section>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <EnquiriesList />
        </div>
      </div>
    </TabsContent>
  );
}
